import { Box, debounce } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import {
  AutoSizer,
  defaultOverscanIndicesGetter,
  GridCellProps,
  Index,
  MultiGrid,
  OverscanIndices,
  OverscanIndicesGetterParams,
  ScrollParams,
} from 'react-virtualized';
import ScrollToTopButton from '../../../../../features/site/components/ScrollToTopButton/ScrollToTopButton';
import {
  CfCellData,
  CfDataGridForStoreDataProps,
  CfDataGridProps,
  CfDataGridSheetViewDefinition,
  NavigableElement,
} from '../../../CfDataGridProps';
import { getFilterText, getHasItems, getItemIds, getItems } from '../../../utility/data-grid-props';
import { CfDataGridNoResultsFound } from '../../DataGridNoResultsFound/CfDataGridNoResultsFound';
import { createValueGetters } from '../../DataGridTableView/body/common';
import { getCellsDataByItem, getCellsDataByItemId, getHeaderCellsData } from './common';

export function CfDataGridSheetViewBody<TItem, TAppState>(props: {
  dataGridProps: CfDataGridProps<TItem> | CfDataGridForStoreDataProps<TItem, TAppState>;
  tableViewDefinition: CfDataGridSheetViewDefinition<TItem>;
  setNavigableElement: (navigableElement: NavigableElement, rowIndex: number, columnIndex: number) => void;
}): JSX.Element {
  const valueGetters = createValueGetters(props.tableViewDefinition.columns);
  const dataGridProps = props.dataGridProps;
  const filterText = getFilterText(dataGridProps);
  const hasItems = getHasItems(dataGridProps);

  const cells: CfCellData[][] = [];
  if (props.tableViewDefinition.showHeader) cells.push(getHeaderCellsData(valueGetters));

  if ('data' in dataGridProps) {
    const items = getItems(dataGridProps.data);
    items.forEach(({ item }, itemIndex: number) => {
      const itemId = item ? dataGridProps.selectId(item) : undefined;
      const newCells = getCellsDataByItem(
        {
          itemIndex,
          filterText,
          setNavigableElement: props.setNavigableElement,
        },
        itemId,
        item,
        valueGetters
      );
      cells.push(newCells);
    });
  } else {
    const itemIds = getItemIds(dataGridProps.storeData);
    itemIds.map(({ itemId }, itemIndex: number) => {
      const newCells = getCellsDataByItemId(
        {
          itemIndex,
          filterText,
          setNavigableElement: props.setNavigableElement,
        },
        itemId,
        dataGridProps.storeData.itemSelector,
        dataGridProps.storeData.useSelector,
        valueGetters
      );
      cells.push(newCells);
    });
  }

  const getRowHeight = ({ index }: Index): number => {
    const firstCellOfRow = cells[index]?.[0];
    if (!firstCellOfRow) return 0;
    return props.tableViewDefinition.getRowHeight(firstCellOfRow.type, firstCellOfRow.id, index);
  };

  const getColumnWidth = ({ index }: Index): number => {
    const getter = valueGetters[index];
    const numericWidth = +(getter.column.width ?? '');
    if (isNaN(numericWidth)) {
      console.error(
        `ERROR - a constant, numeric column width is required for column[${index}] in CfDataGridMultTableView`
      );
      return 0;
    }
    return numericWidth;
  };

  const getGridHeight = (): number => {
    let height = 17;
    for (let index = 0; index < cells.length; index++) height += getRowHeight({ index });
    return height;
  };

  const overscanIndicesGetter = (params: OverscanIndicesGetterParams): OverscanIndices => {
    const result = defaultOverscanIndicesGetter(params);
    // TODO: override behaviour if flickering occurs
    // console.log('defaultOverscanIndicesGetter', params, result);
    return result;
  };
  const multiGridRef = useRef<MultiGrid>(null);
  const parentRef = useRef<HTMLElement>(null);

  const gridColumnWidths = props.tableViewDefinition.columns.map((_c, index) => getColumnWidth({ index })).join('|');

  useEffect(() => {
    multiGridRef.current?.recomputeGridSize();
  }, [gridColumnWidths]);

  // Scroll To Top Properties
  const scrollClass = 'cf-data-grid-sheet-view-body-bottom-right';
  const scrollTarget = parentRef.current?.querySelector(`.${scrollClass}`);
  const scrollThreshold = 0;
  const [isScrollThresholdMet, setIsScrollThresholdMet] = useState<boolean>(false);

  // Reevaluate a threshold property because the reversing useScrollTrigger
  // essentially negates its threshold evaluation.
  const handleScroll = (params: ScrollParams) => {
    if (params.scrollTop > scrollThreshold) {
      setIsScrollThresholdMet(true);
    } else {
      setIsScrollThresholdMet(false);
    }
  };
  const debounceHandleScroll = debounce((params: ScrollParams) => handleScroll(params), 300);
  return (
    <>
      <Box ref={parentRef} sx={{ flex: 1, height: getGridHeight(), ...props.tableViewDefinition.tableBodySx }}>
        <ScrollToTopButton
          scrollTarget={scrollTarget}
          scrollThreshold={scrollThreshold}
          isScrollThresholdMet={isScrollThresholdMet}
        />
        <AutoSizer>
          {({ width, height }) => (
            <MultiGrid
              ref={multiGridRef}
              cellRenderer={getCellRender(cells)}
              width={width}
              height={height}
              columnCount={props.tableViewDefinition.columns.length}
              fixedColumnCount={props.tableViewDefinition.fixedColumnCount}
              fixedRowCount={props.tableViewDefinition.fixedRowCount}
              rowCount={cells.length}
              rowHeight={getRowHeight}
              columnWidth={getColumnWidth}
              overscanColumnCount={20}
              overscanRowCount={10}
              overscanIndicesGetter={overscanIndicesGetter}
              classNameBottomRightGrid={scrollClass}
              onScroll={debounceHandleScroll}
            />
          )}
        </AutoSizer>
      </Box>
      {!hasItems && (
        <CfDataGridNoResultsFound
          message={props.dataGridProps.noItemsMessage}
          sx={props.dataGridProps.noItemsMessageSx}
          displayMode='table'
        />
      )}
    </>
  );
}

const getCellRender = (cells: CfCellData[][]) =>
  function cellRenderer(props: GridCellProps): JSX.Element | null {
    return (
      <Box key={props.key} style={props.style}>
        {cells[props.rowIndex]?.[props.columnIndex]?.content}
      </Box>
    );
  };
